import { useRef, useState } from "react"
import * as S from "./styled"
import { TTicketDisposal } from "../../utils/@types/data/ticket"
import { formatMoney } from "../../utils/tb/formatMoney"
import MesaOption from "../MesaOption"
import { ReactComponent as DropdownIcon } from "../../assets/icons/arrow_down.svg"

const PAGE_SIZE = 10

type Props = {
  products: TTicketDisposal[]
  mesaType: "mesa" | "camarote"
  selectedId: string | null
  onSelect: (productId: string | null) => void
}

const MesaSelect = ({ products, mesaType, selectedId, onSelect }: Props) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Adjusting state during render (not an effect) to mirror the selectedId
  // prop while allowing an immediate local update on click — the parent
  // may not re-render synchronously with the new prop.
  const [prevSelectedId, setPrevSelectedId] = useState(selectedId)
  const [localSelectedId, setLocalSelectedId] = useState(selectedId)
  if (selectedId !== prevSelectedId) {
    setPrevSelectedId(selectedId)
    setLocalSelectedId(selectedId)
  }

  const sorted = [...products].sort(
    (a, b) => (a.mesa_number ?? 0) - (b.mesa_number ?? 0),
  )

  const filtered = search.trim()
    ? sorted.filter((p) => {
        const label = `${mesaType === "mesa" ? "Mesa" : "Camarote"} ${p.mesa_number ?? ""}`
        return label.toLowerCase().includes(search.trim().toLowerCase())
      })
    : sorted

  const visible = filtered.slice(0, visibleCount)

  const selectedProduct = products.find((p) => p.id === localSelectedId) ?? null

  const triggerLabel = selectedProduct
    ? `${mesaType === "mesa" ? "Mesa" : "Camarote"} ${selectedProduct.mesa_number ?? 0} — ${formatMoney(selectedProduct.price_sell, true)}`
    : "Selecione um número..."

  const closeList = () => {
    setOpen(false)
    triggerRef.current?.focus()
  }

  return (
    <S.Component>
      <S.Trigger
        ref={triggerRef}
        type="button"
        $hasSelection={!!selectedProduct}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{triggerLabel}</span>
        <DropdownIcon
          style={{
            width: 20,
            height: 20,
            transition: "transform 0.3s",
            transform: `rotate(${open ? 180 : 0}deg)`,
          }}
        />
      </S.Trigger>
      {open && (
        <S.List data-testid="mesa-select-options">
          <div style={{ paddingBottom: 10 }}>
            <S.SearchInput
              type="text"
              placeholder="Buscar..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setVisibleCount(PAGE_SIZE)
              }}
              data-testid="mesa-select-search"
            />

            {visible.length === 0 && (
              <S.EmptyMessage>Nenhuma opção encontrada</S.EmptyMessage>
            )}
          </div>

          {visible.map((p) => (
            <MesaOption
              key={p.id}
              product={p}
              mesaType={mesaType}
              selected={p.id === localSelectedId}
              onClick={() => {
                const newId = p.id === localSelectedId ? null : p.id
                // Only the deselect path clears locally — a new selection
                // is reflected once the parent re-renders with the updated
                // selectedId prop, keeping this component driven by props.
                if (newId === null) setLocalSelectedId(null)
                onSelect(newId)
                closeList()
              }}
            />
          ))}

          {filtered.length > visibleCount && (
            <S.ShowMoreButton
              type="button"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            >
              Ver mais
            </S.ShowMoreButton>
          )}
        </S.List>
      )}
    </S.Component>
  )
}

export default MesaSelect
